import React from 'react';
import PropTypes, { string, bool, object, number } from 'prop-types';
import styled from 'styled-components';
import { get, map, size, filter, forEach, join, isEmpty, findIndex } from 'lodash';
import moment from 'moment';
import Barcode from 'react-barcode';
import { Row, Column, Image, HasPermissions, NotFound } from '../../src/components/common';
import JumboLogo from '../../assets/images/jumbo_logo.png';
import { OrderShippingEstimate } from '../../src/components/shared';
import { dashZero, baggingEnums } from '../../src/utils/misc';
import {
    OrderPdfWrapper,
    DataField,
    Table,
    Text,
    Title,
    formatProductQuantity
} from '../../src/components/pdf-commons';
import { GetPickerByEmail } from '../../src/components/graphql';
import { getLooseProductsQuantity } from '../../src/components/pdf-commons/utils';
import { urlSisa, urlBase } from './../../src/components/utils/changeUrlSisa';
import SisaLogo from './../../assets/images/sisa.png';

const Signature = styled.div`
    width: 253px;
    height: 50px;
    border-bottom: 2px solid #333333;
`;

const Input = styled.div`
    height: 27px;
    width: 100%;
    border-radius: 3px;
    border: 1px solid #848484;
    background: #FFFFFF;
`;

const Spacer = styled.div`
    width: 27px;
`;

const OrderPDF = ({ match }) => {
    const orderId = get(match, 'params.id', null);

    return (
        <HasPermissions
            to={['order-detail-page:view']}
            otherwise={<NotFound firstTextLine='Página no encontrada' style={{ minHeight: '90vh' }} />}
        >
            <OrderPdfWrapper orderId={orderId}>
                {
                    ({ data }) => {
                        const items = get(data, 'assignInfo.items', {});
                        const baggingDetail = get(data, 'assignInfo.baggingInfo.detail', {});
                        const looseProducts = getLooseProductsQuantity(data);
                        const totalBagging = size(baggingDetail) + looseProducts;
                        return (
                            <>
                                <Header orderId={orderId} data={data} />
                                <BoxesAndBags baggingDetail={baggingDetail} />
                                <LooseProducts items={items} qty={looseProducts} />
                                <TotalBagging count={totalBagging} />
                                <Delivery />
                            </>
                        );
                    }
                }
            </OrderPdfWrapper>
        </HasPermissions>
    );
};

const Field = ({ label, textArea }) => {
    if (textArea) {
        return (
            <div style={{ margin: '3px 0', display: 'flex', flexDirection: 'column', height: '100%' }}>
                <span>{ label }</span>
                <Input style={{ height: '100%' }} />
            </div>
        );
    }
    return (
        <div style={{ margin: '3px 0' }}>
            <span>{ label }</span>
            <Input />
        </div>
    );
};

const Header = ({ data, orderId }) => {
    const baggingDetail = get(data, 'assignInfo.baggingInfo.detail', {});
    const looseProducts = getLooseProductsQuantity(data);
    const selectedSla = get(data, 'logisticsInfo.selectedSla', '');
    const selectedSlaLabel = () => {
        switch (selectedSla) {
        case 'Despacho Especial':
        case 'Despacho Express':
        case 'Despacho a Domicilio':
            return 'Dirección de despacho';
        default:
            return 'Dirección de retiro';
        }
    };
    let changeUrl = false;
    if (urlSisa() == urlBase()) {
        changeUrl = true;
    }
    return (
        <>
            <Row>
                <Column direction='row' flex={3}>
                    <Text text={`Hoja de despacho ${orderId}`} size={20} strong />
                </Column>
                <Column align='flex-end' justify='center'>
                    <Image src={changeUrl ? SisaLogo : JumboLogo} height={19} width={100} grayscale={100} contrast={1000} />
                </Column>
            </Row>
            <Row style={{ borderRadius: '3px', width: '100%', border: '1px solid #000000' }} mT={10}>
                <OrderShippingEstimate {...data}>
                    { ({ deliveryTime, deliveryTimeHourBySla }) => (
                        <>
                            <Column style={{ display: 'block' }} mY={15} mL={15} mR={20} flex={5}>
                                <DataField
                                    label='Nombre del cliente'
                                    value={`${get(data, 'customer.firstName', '')} ${get(data, 'customer.lastName', '')}`}
                                />
                                <DataField
                                    label='RUT del cliente'
                                    value={`${get(data, 'customer.document', '')}`}
                                />
                                <DataField label='Teléfono' value={`${get(data, 'customer.phone', '')}`} />
                                <DataField label={selectedSlaLabel()} value={`${get(data, 'selectedAddress.addressLine', '')}`} />
                                <DataField label='Quien recibe' value={`${get(data, 'selectedAddress.receiverName', '')}`} />
                                <DataField label='Zona' value={`${get(data, 'logisticsInfo.deliveryIds[0].courierId', '')}`} />
                                <DataField label='Tipo de despacho' value={selectedSla} />
                                <DataField label='Fecha de compra' value={moment(get(data, 'createdAt', '')).format('DD-MM-YYYY')} />
                                <DataField label='Observaciones' value={`${get(data, 'selectedAddress.reference', '-') || '-'}`} />
                            </Column>
                            <Column style={{ display: 'block' }} mT={15} mR={15} flex={4}>
                                <GetPickerByEmail email={get(data, 'assignInfo.idUser', 'null')}>
                                    { ({ fullName }) => <DataField label='Shopper' value={fullName} /> }
                                </GetPickerByEmail>
                                <DataField label='Embolsados' value={`${baggingDetail.length}`} />
                                <DataField label='Productos sueltos' value={`${looseProducts}`} />
                                <DataField label='Total bultos' value={dashZero(((baggingDetail && baggingDetail.length) || 0) + looseProducts)} />
                                <DataField
                                    label='Fecha de compromiso'
                                    value={`${moment(get(data, 'logisticsInfo.shippingETA', ''), 'YYYY-MM-DD HH:mm:ss').format('DD-MM-YYYY')}`}
                                />
                                <DataField label='Promesa de entrega' value={deliveryTime} />
                                <DataField label='Hora de facturación' value={`${get(data, 'assignInfo.posDetail.0.hour', '-')}`} />
                                <Barcode
                                    value={get(data, 'assignInfo.eanOrder', 'server code error')}
                                    width={1.6}
                                    height={60}
                                    fontSize={14}
                                    marginTop={10}
                                />
                            </Column>
                        </>
                    )}
                </OrderShippingEstimate>
            </Row>
        </>
    );
};

const BoxesAndBags = ({ baggingDetail }) => {
    let sortedByType = [];
    forEach(baggingDetail, ({ type, randomId, id }) => {
        const index = findIndex(sortedByType, ['type', type]);
        if (index < 0) {
            sortedByType = [...sortedByType, { type, value: [`${randomId ? '-' : id}`] }];
        } else {
            sortedByType[index] = { type: get(sortedByType[index], 'type'), value: [...get(sortedByType[index], 'value'), `${randomId ? '-' : id}`] };
        }
    });
    const data = {
        headers: [
            { key: 'qty', value: 'Cant' },
            { key: 'type', value: 'Formato' },
            { key: 'code', value: 'Código' }
        ],
        body: map(sortedByType, ({ type, value }) => {
            return {
                qty: `${size(value)} un`,
                type: baggingEnums[type],
                code: join(value, ' - ')
            };
        })
    };
    return !isEmpty(get(data, 'body', [])) && <Table counter={size(baggingDetail)} title='Embolsados' headers={data.headers} body={data.body} />;
};

const LooseProducts = ({ items, qty }) => {
    let areLoose = filter(items, ({ unpackaged }) => unpackaged === true);
    let substitutesLoose = [];
    items.forEach(({ substitution }) => {
        const filtered = filter(substitution, ({ unpackaged }) => unpackaged === true);
        substitutesLoose = [...substitutesLoose, ...filtered];
    });
    areLoose = [...areLoose, ...substitutesLoose];
    const body = map(areLoose, ({ pickingQuantity, unit, ean, name }) => {
        return {
            qty: formatProductQuantity(pickingQuantity, unit),
            cod: ean,
            req: name
        };
    });
    const data = {
        headers: [
            { key: 'qty', value: 'Cant' },
            { key: 'cod', value: 'Código' },
            { key: 'req', value: 'Producto solicitado' }
        ],
        body
    };
    return size(body) > 0 && <Table counter={qty} title='Productos sueltos' headers={data.headers} body={data.body} />;
};

const TotalBagging = ({ count }) => {
    if (!count) return null;
    return (
        <Row style={{ borderBottom: '2px solid #333333', borderTop: '2px solid #333333', lineHeight: '2.7', margin: '15px 0 60px 0' }}>
            <Column>
                <Title marginTop={0} text='Total bultos' />
            </Column>
            <Column noFlex>
                <Title marginTop={0} text={count} />
            </Column>
        </Row>
    );
};

const Delivery = () => (
    <div style={{ pageBreakInside: 'avoid' }}>
        <div style={{ borderBottom: '2px solid #333333', lineHeight: '2.7' }}>
            <Title text='Antecedentes de la entrega' />
        </div>
        <Row mT={22}>
            <Column noWrap>
                <Field label='Quien recibe' />
                <Row>
                    <Column>
                        <Field label='Fecha entrega' />
                    </Column>
                    <Spacer />
                    <Column>
                        <Field label='Hora entrega' />
                    </Column>
                </Row>
                <Row>
                    <Column>
                        <Field label='Cantidad productos sueltos' />
                    </Column>
                    <Spacer />
                    <Column>
                        <Field label='Cantidad de bultos' />
                    </Column>
                </Row>
                <Field label='Sello de seguridad' />
            </Column>
            <Spacer />
            <Column noWrap>
                <Field label='RUT' />
                <Field label='Alteradas' />
                <Field label='Comentarios' textArea />
            </Column>
        </Row>
        <Row>
            <Column align='center'>
                <Signature />
                <Text size={11} text='Firma persona quien recibe' />
            </Column>
        </Row>
    </div>
);

OrderPDF.propTypes = {
    match: PropTypes.object
};

Field.propTypes = {
    label: string,
    textArea: bool
};

Header.propTypes = {
    data: object,
    orderId: string
};

LooseProducts.propTypes = {
    items: object
};

TotalBagging.propTypes = {
    count: number
};

export default OrderPDF;
